import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { TransportOrder } from "../../model/transportorder/transportOrder"

export const INITIAL_RENDER_COUNT = 100
export const ROWS_PER_SCROLL_BATCH = 50
export const SCROLL_LOAD_TRIGGER_TAIL_RATIO = 0.3
export const RETURN_ROW_HIGHLIGHT_DURATION_MS = 5000
export type OrderOverviewReturnState = {
    scrollPositionToId: string
    visibleCount: number
}

type UseOrderOverviewInfiniteRenderingParams = {
    transportOrders: TransportOrder[]
    overviewReturnState: OrderOverviewReturnState | undefined
    isFetchingTransportOrders: boolean
}

type UseOrderOverviewInfiniteRenderingResult = {
    effectiveVisibleCount: number
    visibleTransportOrders: TransportOrder[]
    scrollLoadTriggerTransportOrderId: string | undefined
    setScrollLoadTriggerElement: (element: HTMLTableRowElement | null) => void
    resetVisibleCount: () => void
}

export const useOrderOverviewInfiniteRendering = (
    params: UseOrderOverviewInfiniteRenderingParams
): UseOrderOverviewInfiniteRenderingResult => {
    const { transportOrders, overviewReturnState, isFetchingTransportOrders } = params
    const location = useLocation()
    const navigate = useNavigate()
    const [visibleCount, setVisibleCount] = useState<number>(overviewReturnState?.visibleCount ?? INITIAL_RENDER_COUNT)
    const effectiveVisibleCount = Math.min(visibleCount, transportOrders.length)
    const visibleTransportOrders = transportOrders.slice(0, effectiveVisibleCount)
    const scrollLoadTriggerIndex = Math.max(
        0,
        visibleTransportOrders.length - Math.ceil(visibleTransportOrders.length * SCROLL_LOAD_TRIGGER_TAIL_RATIO)
    )
    const scrollLoadTriggerTransportOrderId = visibleTransportOrders[scrollLoadTriggerIndex]?.id
    const [scrollLoadTriggerElement, setScrollLoadTriggerElement] = useState<HTMLTableRowElement | null>(null)
    const latestVisibleCountRef = useRef(effectiveVisibleCount)
    const latestTransportOrderCountRef = useRef(transportOrders.length)
    const isScrollBatchUpdatePendingRef = useRef(false)
    const hasHandledReturnScrollRef = useRef(false)
    const highlightTimeoutRef = useRef<number | undefined>(undefined)

    useEffect(() => {
        latestVisibleCountRef.current = effectiveVisibleCount
        latestTransportOrderCountRef.current = transportOrders.length
        isScrollBatchUpdatePendingRef.current = false
    }, [effectiveVisibleCount, transportOrders.length])

    useEffect(() => {
        return () => {
            if (highlightTimeoutRef.current !== undefined) {
                window.clearTimeout(highlightTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (!scrollLoadTriggerElement || typeof IntersectionObserver === "undefined") {
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                const shouldLoadMore = entries.some(entry => entry.isIntersecting)
                if (!shouldLoadMore || isScrollBatchUpdatePendingRef.current) {
                    return
                }

                const latestVisibleCount = latestVisibleCountRef.current
                const latestTransportOrderCount = latestTransportOrderCountRef.current
                if (latestVisibleCount >= latestTransportOrderCount) {
                    return
                }

                isScrollBatchUpdatePendingRef.current = true
                setVisibleCount(current => current + ROWS_PER_SCROLL_BATCH)
            },
            { threshold: 0 }
        )

        observer.observe(scrollLoadTriggerElement)

        return () => {
            observer.disconnect()
        }
    }, [isFetchingTransportOrders, scrollLoadTriggerElement, transportOrders.length])

    useEffect(() => {
        if (!overviewReturnState || hasHandledReturnScrollRef.current) {
            return
        }

        const element = document.getElementsByClassName(overviewReturnState.scrollPositionToId)
        if (element.length === 0) {
            return
        }

        element[0].scrollIntoView({
            behavior: "auto",
            block: "center",
            inline: "nearest",
        })

        const rowElement = element[0] as HTMLElement
        rowElement.classList.add("active")
        hasHandledReturnScrollRef.current = true

        if (highlightTimeoutRef.current !== undefined) {
            window.clearTimeout(highlightTimeoutRef.current)
        }

        highlightTimeoutRef.current = window.setTimeout(() => {
            rowElement.classList.remove("active")
            highlightTimeoutRef.current = undefined
        }, RETURN_ROW_HIGHLIGHT_DURATION_MS)

        navigate({
            pathname: location.pathname,
            search: location.search,
        }, { replace: true })
    }, [effectiveVisibleCount, location.pathname, location.search, navigate, overviewReturnState, visibleTransportOrders.length])

    return {
        effectiveVisibleCount,
        visibleTransportOrders,
        scrollLoadTriggerTransportOrderId,
        setScrollLoadTriggerElement,
        resetVisibleCount: () => {
            setVisibleCount(INITIAL_RENDER_COUNT)
        },
    }
}
